import type { IPostTrainingAppliesCandidateResponseItem } from "@api";
import type { TJSONSchema } from "@artifact-management/json-dynamic-form";
import { TrainingApplyStatus } from "@bbj-types/apply/training";
import { ModalTrainingApply } from "@components/trainingOffer/ModalTrainingApply";
import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  autocompleteClasses,
} from "@mui/material";
import { type FieldValues, type Path, type PathValue, useController } from "react-hook-form";
import type { IFormGenInputProps } from "../index";
import { type TTrainingApplyOption, useFormGenInputTrainingApply } from "./logics";

const isTrainingApplyOption = (value: unknown): value is TTrainingApplyOption => {
  return (
    typeof value === "object" &&
    value !== null &&
    "label" in value &&
    "subText" in value &&
    "value" in value &&
    typeof value.label === "string" &&
    typeof value.subText === "string" &&
    typeof value.value === "string"
  );
};

const isTrainingApplyOptionArray = (value: unknown): value is TTrainingApplyOption[] => {
  return Array.isArray(value) && value.every(isTrainingApplyOption);
};

export const FormGenInputTrainingApply = <Schema extends TJSONSchema, T extends FieldValues>({
  control,
  uiSchemaElement,
  jsonSchemaElement,
  context,
  multiple,
}: IFormGenInputProps<Schema, T>) => {
  const trainingApplyOptions = jsonSchemaElement.trainingApply;
  if (!trainingApplyOptions) {
    throw new Error(`Missing 'trainingApply' keyword on the field schema for scope: ${uiSchemaElement.scope}`);
  }
  const {
    autocompleteOpen,
    setAutocompleteOpen,
    handleNewApplyClick,
    options,
    loading,
    applyModalOpen,
    setApplyModalOpen,
    handleNewApply,
  } = useFormGenInputTrainingApply(trainingApplyOptions, context);

  const { field, fieldState } = useController({
    control,
    name: uiSchemaElement.scope as unknown as Path<T>,
    defaultValue: (multiple ? [] : null) as unknown as PathValue<T, Path<T>>,
  });
  const handleConfirmNewApply = (createdTrainingApply: IPostTrainingAppliesCandidateResponseItem) => {
    const createdOption = handleNewApply(createdTrainingApply);
    if (multiple) {
      const fieldValue: unknown = field.value;
      const currentValue: TTrainingApplyOption[] = isTrainingApplyOptionArray(fieldValue) ? fieldValue : [];
      const nextValue: TTrainingApplyOption[] = [
        createdOption,
        ...currentValue.filter((option) => option.value !== createdOption.value),
      ];
      field.onChange(nextValue);
      return;
    }

    field.onChange(createdOption);
  };

  return (
    <>
      <Autocomplete<TTrainingApplyOption, boolean, false, false>
        open={autocompleteOpen}
        onOpen={() => setAutocompleteOpen(true)}
        onClose={() => setAutocompleteOpen(false)}
        value={field.value as TTrainingApplyOption | TTrainingApplyOption[] | null}
        onChange={(_, value) => {
          field.onChange(value);
        }}
        options={options}
        loading={loading}
        onBlur={field.onBlur}
        disabled={field.disabled}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        limitTags={1}
        size="small"
        fullWidth
        getOptionLabel={(option) => option?.label ?? ""}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) {
            return false;
          }

          return option.value === value.value;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={uiSchemaElement.label}
            name={field.name}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
            inputRef={field.ref}
          />
        )}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <MenuItem
              key={key}
              {...optionProps}
              component="li"
              selected={selected}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start !important",
              }}
            >
              <Typography variant="subtitle2">{option.label}</Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "black.light",
                }}
              >
                {option.subText}
              </Typography>
            </MenuItem>
          );
        }}
        slots={{
          paper: ({ children, ...props }) => (
            <Paper
              {...props}
              sx={{
                [`& .${autocompleteClasses.listbox}`]: {
                  pb: 0,
                },
              }}
            >
              {children}
              <Divider sx={{ mx: 2, my: 1 }} />
              <MenuItem
                component="div"
                onClick={handleNewApplyClick}
                onMouseDown={(evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                }}
                sx={{
                  px: 2,
                  py: 1,
                  mb: 1,
                }}
              >
                <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}>
                  <AddIcon color="primary" />
                  <Typography variant="subtitle1SemiBold" color="primary">
                    Renseigner une nouvelle formation intégrée
                  </Typography>
                </Stack>
              </MenuItem>
            </Paper>
          ),
        }}
      />
      <ModalTrainingApply
        open={applyModalOpen}
        defaultValues={{
          status: TrainingApplyStatus.INTEGRATED_CANDIDATE,
        }}
        disabledFields={{ status: true }}
        onCreate={handleConfirmNewApply}
        onClose={() => setApplyModalOpen(false)}
      />
    </>
  );
};
